/**
 * Miscellaneous additions — water salts, acids, alkalis, finings, nutrients,
 * water treatment, enzymes, hop-flavour products and processing aids.
 *
 * Ion contributions and acid equivalence maths deliberately live in
 * `@beerwright/core` (PLAN §5.4/§5.5); this package supplies the identity,
 * concentration and dosing guidance the editor needs to offer the addition in
 * the first place.
 */

import { z } from 'zod';
import {
  descriptionSchema,
  productUrlSchema,
  provenanceSchema,
  slugSchema,
  tagsSchema,
} from './common';

/** What kind of thing it is. Drives which panel of the editor offers it. */
export const miscTypeSchema = z.enum([
  /** Brewing salt: adjusts the mineral profile (and, incidentally, pH). */
  'water-agent',
  /** Lowers pH. Carries a {@link miscSchema} `concentrationPercent`. */
  'acid',
  /** Raises pH — bicarbonate, carbonate, hydroxide. */
  'alkali',
  /** Clarifier: kettle, cold-side or both. */
  'fining',
  /** Yeast nutrient / energiser. */
  'nutrient',
  /** Chloramine/chlorine removal and other pre-treatment. */
  'water-treatment',
  /** Mash, fermentation or packaging enzyme. */
  'enzyme',
  /** Spices, herbs, botanicals. */
  'spice',
  /**
   * Hop flavour without the hop: terpene blends and aroma concentrates, dosed
   * by volume into finished beer.
   *
   * Its own member rather than `spice` (nothing here is a botanical) or `other`
   * (which says nothing at all), because a brewer looking for one is looking
   * for this category by name — and because the picker's signpost under Hops
   * has to have something to point at.
   */
  'hop-flavour',
  /** Anything that fits nowhere else. */
  'other',
]);

/** @see miscTypeSchema */
export type MiscType = z.infer<typeof miscTypeSchema>;

/** The stage the addition is normally made at. */
export const miscUseSchema = z.enum([
  'water',
  'mash',
  'sparge',
  'boil',
  'whirlpool',
  'primary',
  'secondary',
  'packaging',
]);

/** @see miscUseSchema */
export type MiscUse = z.infer<typeof miscUseSchema>;

/** Physical form as sold. */
export const miscFormSchema = z.enum(['powder', 'granular', 'liquid', 'tablet', 'solid']);

/** @see miscFormSchema */
export type MiscForm = z.infer<typeof miscFormSchema>;

/** One miscellaneous addition. */
export const miscSchema = z.strictObject({
  /** Stable slug, e.g. `gypsum`, `lactic-acid-88`. */
  id: slugSchema,
  /** Common name as brewers ask for it. */
  name: z.string().min(2).max(60),
  /** @see miscTypeSchema */
  type: miscTypeSchema,
  /** @see miscUseSchema */
  use: miscUseSchema,
  /** @see miscFormSchema */
  form: miscFormSchema,
  /**
   * Minutes of contact at the stage in `use`.
   *
   * For boil additions this is minutes before knockout (Irish moss at 15);
   * for cold-side additions it is contact time. `null` where timing is not
   * meaningful (salts dissolved into the water up front).
   */
  timingMinutes: z.number().min(0).max(20160).nullable(),
  /** How the timing is normally expressed, in words. */
  timingNote: z.string().min(4).max(200),
  /**
   * Strength of a solution, % w/w — 88 for standard lactic, 10/75/85 for the
   * phosphoric grades. `null` for solids sold as the pure compound.
   */
  concentrationPercent: z.number().min(0.1).max(100).nullable(),
  /** Formula including water of crystallisation, e.g. `CaSO₄·2H₂O`. */
  chemicalFormula: z.string().min(2).max(40).nullable(),
  /** Typical dose in words, e.g. `0.5–1 g/L of mash water`. */
  typicalDosage: z.string().min(3).max(120).nullable(),
  /**
   * The same dose as a number, **per litre of batch**, so the app can weigh it
   * out instead of making the brewer read {@link typicalDosage} and do it.
   *
   * The unit is whichever one `form` resolves to when the addition is snapshot
   * into a recipe — `tablet` counts in `each`, `liquid` in millilitres and
   * everything else in grams. Keeping it implicit rather than storing a unit
   * beside it means the two can never disagree.
   *
   * `null` where a number would be a lie, which is most of the catalog. Salts
   * are dosed to a water profile rather than a volume, acids to a measured pH,
   * and finings like gelatine to how hazy the beer actually turned out. Only
   * fill this in where the maker publishes a flat rate per volume, and where
   * the published rate is a band, take its middle — {@link typicalDosage} still
   * carries the band, and the brewer can move the number.
   */
  dosePerLiter: z.number().positive().max(100).nullable(),
  /** Our own words. */
  description: descriptionSchema,
  /** Anything a brewer should know before using it — hydration, safety, form. */
  note: z.string().min(4).max(400).nullable(),
  /** Search tags. */
  tags: tagsSchema,
  /** @see provenanceSchema */
  /** @see productUrlSchema */
  url: productUrlSchema.optional(),
  provenance: provenanceSchema,
});

/** @see miscSchema */
export type Misc = z.infer<typeof miscSchema>;
